import type { HomeThreadResponse, WorkflowDocumentHistory } from './types';
import type { ManualEditAiHistoryTurn } from '../../api/manualEditAi';
import type { DocumentConversationTurn } from './DocumentAssistantContext';

export type WorkflowDocumentHistoryState = { sessionId: string; items: WorkflowDocumentHistory[]; error?: string };
const kinds = new Set(['construction_review', 'construction_generated', 'services_review', 'services_generated']);

/** Only accept history that the current owned thread explicitly associates. */
export function normalizeWorkflowDocumentHistory(thread: HomeThreadResponse, sessionId: string): WorkflowDocumentHistoryState {
  return { sessionId, items: (Array.isArray(thread.workflow_document_histories) ? thread.workflow_document_histories : [])
    .filter(item => item?.home_chat_session_id === sessionId && typeof item.flow_id === 'string' && kinds.has(item.document_kind) && Array.isArray(item.history?.turns)),
    ...(thread.workflow_document_history_error ? { error: thread.workflow_document_history_error } : {}) };
}

/** Shared source identity prevents duplicate sidebar/canonical history records. */
export function documentHistoryTurn(turn: ManualEditAiHistoryTurn, source: { flowId: string; documentKind: string; documentLabel: string }): DocumentConversationTurn {
  return { id: `document_edit:${source.flowId}:${source.documentKind}:${turn.id}`, request: turn.request,
    completedAt: turn.completed_at, submittedBy: turn.submitted_by, documentLabel: source.documentLabel,
    result: { status: turn.status, summary: turn.summary, changes: turn.changes, checkpoint_id: turn.checkpoint_id,
      document_fingerprint: turn.document_fingerprint_after || turn.document_fingerprint_before, history_turn: turn } };
}

export function projectedDocumentHistory(items: WorkflowDocumentHistory[]): DocumentConversationTurn[] {
  const seen = new Set<string>();
  return items.flatMap(item => item.history.turns.map(turn => documentHistoryTurn(turn, {
    flowId: item.flow_id, documentKind: item.document_kind, documentLabel: item.document_label,
  }))).filter(turn => { if (seen.has(turn.id)) return false; seen.add(turn.id); return true; });
}
